/**
 * OmniBar — ⌘K jump-or-capture overlay, ported (trimmed) from loop.
 * Type to search contacts / companies / meetings-events-leads / records;
 * type `/` as the first character and the overlay hands off to the capture
 * bar. Enter jumps; picking a record opens its edit modal (records have no
 * detail route, matching loop).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
    companies,
    contacts,
    leads,
    loopRecords,
    type LoopRecord,
} from "../../lib/api";
import { directorySearchQueryKey } from "../../lib/query-keys";
import { useLoopDirectory } from "./useDirectory";
import CaptureBar from "./CaptureBar";
import EditRecordModal from "./EditRecordModal";

const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

interface OmniItem {
    key: string;
    group: string;
    label: string;
    sub?: string;
    action: () => void;
}

const TAG_ROUTE: Record<string, string> = {
    company: "/companies", lead: "/pipeline", event: "/events", meeting: "/meetings",
};

export default function OmniBar({ open, onClose }: { open: boolean; onClose: () => void }) {
    const navigate = useNavigate();
    const { directory } = useLoopDirectory();
    const [text, setText] = useState("");
    const [serverQuery, setServerQuery] = useState("");
    const [index, setIndex] = useState(0);
    const [editRecord, setEditRecord] = useState<LoopRecord | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) { setText(""); setIndex(0); setEditRecord(null); }
    }, [open]);

    const captureMode = text.startsWith("/");
    const q = text.trim().toLowerCase();

    useEffect(() => {
        if (!open || captureMode || q.length < 2) {
            setServerQuery("");
            return;
        }
        const timer = setTimeout(() => setServerQuery(q), 200);
        return () => clearTimeout(timer);
    }, [open, captureMode, q]);

    const serverSearchEnabled = open && !captureMode && serverQuery.length >= 2 && serverQuery === q;

    const contactsSearch = useQuery({
        queryKey: directorySearchQueryKey("contacts", "omni", serverQuery),
        queryFn: async () => (await contacts.searchAll(serverQuery)).slice(0, 100),
        enabled: serverSearchEnabled,
        staleTime: 15_000,
    });
    const companiesSearch = useQuery({
        queryKey: directorySearchQueryKey("companies", "omni", serverQuery),
        queryFn: async () => (await companies.list({ search: serverQuery, limit: "100", sort: "name" })).data,
        enabled: serverSearchEnabled,
        staleTime: 15_000,
    });
    const leadsSearch = useQuery({
        queryKey: directorySearchQueryKey("leads", "omni", serverQuery),
        queryFn: async () => (await leads.list({ search: serverQuery, limit: "100" })).data,
        enabled: serverSearchEnabled,
        staleTime: 15_000,
    });

    const recordsQuery = useQuery({
        queryKey: ["loop", "omni-records", serverQuery],
        queryFn: () => loopRecords.recent(serverQuery, 8),
        enabled: serverSearchEnabled,
        staleTime: 15_000,
    });
    const recordRows = recordsQuery.data?.ok ? recordsQuery.data.records : [];

    const items = useMemo<OmniItem[]>(() => {
        if (captureMode || q.length < 2 || !serverSearchEnabled) return [];
        const out: OmniItem[] = [];
        const match = (s: string) => s.toLowerCase().includes(q);
        for (const c of contactsSearch.data ?? []) {
            const label = c.name || c.email;
            if (!label) continue;
            out.push({
                key: `c:${c.id}`, group: "Contacts", label,
                sub: c.company_label ?? undefined,
                action: () => { navigate(`/contacts/${c.id}`); onClose(); },
            });
            if (out.length >= 6) break;
        }
        const crmMatches = [
            ...(companiesSearch.data ?? []).map(company => ({
                id: company.id, name: company.name, kind: "company" as const,
            })),
            ...(leadsSearch.data ?? []).map(lead => ({
                id: lead.id, name: lead.title, kind: "lead" as const,
            })),
            ...directory.targets.filter(target =>
                (target.kind === "event" || target.kind === "meeting") && match(target.name)),
        ];
        let n = 0;
        for (const t of crmMatches) {
            out.push({
                key: `t:${t.kind}:${t.id}`, group: "CRM", label: t.name, sub: t.kind,
                action: () => { navigate(TAG_ROUTE[t.kind] ?? "/"); onClose(); },
            });
            if (++n >= 6) break;
        }
        for (const r of recordRows) {
            out.push({
                key: `r:${r.id}`, group: "Records", label: r.content,
                sub: [r.type, r.status !== "open" ? r.status : null, r.dueDate ? `due ${r.dueDate}` : null]
                    .filter(Boolean).join(" · "),
                action: () => setEditRecord(r),
            });
        }
        return out.slice(0, 18);
    }, [captureMode, q, serverSearchEnabled, contactsSearch.data, companiesSearch.data, leadsSearch.data, directory.targets, recordRows, navigate, onClose]);

    const isSearching = open && !captureMode && q.length >= 2 && (
        !serverSearchEnabled
        || contactsSearch.isFetching
        || companiesSearch.isFetching
        || leadsSearch.isFetching
        || recordsQuery.isFetching
    );

    useEffect(() => setIndex(0), [q]);

    if (!open) return null;

    return (
        <div className="loop-omni-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="loop-omni" role="dialog" aria-label="Jump or capture">
                {captureMode ? (
                    <>
                        <div className="loop-omni-hint muted">⌘K · Capture — Enter saves, Esc closes</div>
                        <CaptureBar
                            directory={directory}
                            dayPage={localToday()}
                            initialText={text}
                            autoFocus
                            onCreated={onClose}
                        />
                    </>
                ) : (
                    <>
                        <input
                            ref={inputRef}
                            autoFocus
                            className="loop-omni-input"
                            placeholder="Jump to a contact, company, meeting, record… or type / to capture"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
                                if (e.key === "ArrowDown") { e.preventDefault(); setIndex((i) => Math.min(i + 1, items.length - 1)); return; }
                                if (e.key === "ArrowUp") { e.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); return; }
                                if (e.key === "Enter" && items[index]) { e.preventDefault(); items[index]!.action(); }
                            }}
                        />
                        <div className="loop-omni-results">
                            {items.map((item, i) => {
                                const firstOfGroup = i === 0 || items[i - 1]!.group !== item.group;
                                return (
                                    <div key={item.key}>
                                        {firstOfGroup && <div className="loop-omni-group muted">{item.group}</div>}
                                        <button
                                            className={i === index ? "active" : ""}
                                            onMouseEnter={() => setIndex(i)}
                                            onMouseDown={(e) => { e.preventDefault(); item.action(); }}
                                        >
                                            <span className="loop-omni-label">{item.label}</span>
                                            {item.sub && <span className="muted">{item.sub}</span>}
                                        </button>
                                    </div>
                                );
                            })}
                            {q.length >= 2 && items.length === 0 && isSearching && (
                                <div className="loop-omni-group muted">Searching…</div>
                            )}
                            {q.length >= 2 && items.length === 0 && !isSearching && (
                                <div className="loop-omni-group muted">No matches.</div>
                            )}
                            {q.length < 2 && (
                                <div className="loop-omni-group muted">Type to search · / to capture</div>
                            )}
                        </div>
                    </>
                )}
                {editRecord && (
                    <EditRecordModal
                        record={editRecord}
                        open={!!editRecord}
                        onOpenChange={(o) => { if (!o) { setEditRecord(null); onClose(); } }}
                    />
                )}
            </div>
        </div>
    );
}
